import React, { useEffect, useState, useRef } from 'react';
import { FaSort, FaSortUp, FaSortDown, FaCalendarTimes, FaClock, FaPlus, FaFilter, FaCalendarAlt, FaUser } from 'react-icons/fa';
import { ArrowUpDown, ArrowDown, ArrowUp } from 'lucide-react';

import './AppointmentsListTable.css';
import axios from 'axios';
import ENDPOINTS from '../../Constants/ApiEndpoints';
import { dateTimeFormatter } from '../../CommonComponents/Utils/DateTimeFormatter';
import DateRangePickerComponent from '../../CommonComponents/DateRangePickerComponent';
import SearchBar from '../Inpatient/SearchBar';
import { Pagination, PaginationItem } from '@mui/material';

export default function AppointmentsListTable({
    startDate,
    endDate,
    onDateChange,
    onSearch,
    appointmentFilters,
    onNewAppointment,
    onSelectPatient
}) {
    const [records, setRecords] = useState([]);
    const [loading, setLoading] = useState(true);

    const [showPatientFilter, setShowPatientFilter] = useState(false);
    const [patientFilter, setPatientFilter] = useState('');

    const [selectedInvoiceTypes, setSelectedInvoiceTypes] = useState([]);
    const [selectedStatuses, setSelectedStatuses] = useState([]);
    const [hoveredRowIndex, setHoveredRowIndex] = useState(null);
    const [popoverDirMap, setPopoverDirMap] = useState({});

    const handleRowMouseEnter = (e, index) => {
        setHoveredRowIndex(index);
        const rect = e.currentTarget.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;

        // Popover card height is ~240px. If space below is less than 250px and there is room above, show above.
        if (spaceBelow < 250 && spaceAbove > 200) {
            setPopoverDirMap((prev) => ({ ...prev, [index]: 'above' }));
        } else {
            setPopoverDirMap((prev) => ({ ...prev, [index]: 'below' }));
        }
    };

    const handleRowMouseLeave = () => {
        setHoveredRowIndex(null);
    };

    const [waitTimeSortOrder, setWaitTimeSortOrder] = useState(null);
    const [activeTab, setActiveTab] = useState("Pending Appointments");

    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const [counts, setCounts] = useState({ pending: 0, completed: 0, future: 0, cancelled: 0 });

    const activeTabRef = useRef(activeTab);
    useEffect(() => {
        activeTabRef.current = activeTab;
    }, [activeTab]);

    const tabStatusMap = {
        "Pending Appointments": "pending",
        "Completed Appointments": "completed",
        "Future Appointments": "future"
    };

    const formatDate = (date) => {
        const pad = (n) => n.toString().padStart(2, '0');
        return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
    };

    const fetchData = async (currentPage = page, currentSize = pageSize) => {
        const targetTab = activeTabRef.current;
        setLoading(true);

        try {
            const params = {
                fromDate: formatDate(startDate),
                toDate: formatDate(endDate),
                status: tabStatusMap[targetTab] || "pending",
                page: currentPage,
                size: currentSize,
            };

            if (appointmentFilters?.type && appointmentFilters?.query) {
                if (appointmentFilters.type === 'MR No') params.mrNumber = appointmentFilters.query;
                if (appointmentFilters.type === 'Name') params.name = appointmentFilters.query;
                if (appointmentFilters.type === 'Phone') params.phoneNumber = appointmentFilters.query;
            }

            const response = await axios.get(`${ENDPOINTS.SEARCH_APPOINTMENTS_BY_FILTERS}`, { params });

            if (activeTabRef.current !== targetTab) return;

            let fetchedRecords = response.data.records || [];

            const fetchBillClearances = async (recordsList) => {
                if (!recordsList?.length) return recordsList;
                const promises = recordsList.map(r =>
                    axios
                        .get(`${ENDPOINTS.GET_BILL_CLEARANCE}/${r.mrNumber}`)
                        .then(res => res.data)
                        .catch(err => {
                            console.error(`Bill clearance fetch failed for MR ${r.mrNumber}:`, err?.message || err);
                            return null;
                        })
                );
                const results = await Promise.all(promises);
                return recordsList.map((r, i) => ({ ...r, billClearance: results[i] }));
            };

            fetchedRecords = await fetchBillClearances(fetchedRecords);

            if (activeTabRef.current !== targetTab) return;

            setRecords(fetchedRecords);

            setCounts({
                pending: response.data.pending || 0,
                completed: response.data.completed || 0,
                future: response.data.future || 0,
                cancelled: response.data.cancelled || 0,
            });

            setTotalPages(response.data.totalPages || 0);
            setTotalElements(response.data.totalElements || 0);
        } catch (error) {
            console.error('Error fetching Appointments data:', error);
            if (activeTabRef.current === targetTab) {
                setRecords([]);
            }
        } finally {
            if (activeTabRef.current === targetTab) {
                setLoading(false);
            }
        }
    };

    const isFirstRender = useRef(true);
    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        setPage(0);
    }, [startDate, endDate, appointmentFilters, activeTab]);

    useEffect(() => {
        fetchData(page, pageSize);
    }, [page, pageSize, startDate, endDate, appointmentFilters, activeTab]);

    useEffect(() => {
        let tempRecords = [...records];
        if (selectedInvoiceTypes.length > 0) {
            tempRecords = tempRecords.filter((r) =>
                selectedInvoiceTypes.includes(r.type)
            );
        }

        if (selectedStatuses.length > 0) {
            tempRecords = tempRecords.filter((r) =>
                selectedStatuses.includes(r.status)
            );
        }

        if (waitTimeSortOrder) {
            tempRecords.sort((a, b) => {
                const wtA = parseFloat(a.waitTime) || 0;
                const wtB = parseFloat(b.waitTime) || 0;
                return waitTimeSortOrder === 'asc' ? wtA - wtB : wtB - wtA;
            });
        }
    }, [patientFilter, selectedInvoiceTypes, selectedStatuses, records, waitTimeSortOrder]);

    const toggleWaitTimeSort = () => {
        setWaitTimeSortOrder((prev) =>
            prev === 'asc' ? 'desc' : prev === 'desc' ? null : 'asc'
        );
    };

    const calculateWaitTime = (appointmentDate, timeSlot) => {
        if (!appointmentDate || !timeSlot) return { text: "N/A", isUpcoming: true };

        const now = new Date();
        const appointmentTime = new Date(`${appointmentDate}T${timeSlot}`);
        const diffInMs = appointmentTime - now;

        if (diffInMs > 0) {
            const hours = String(Math.floor(diffInMs / (1000 * 60 * 60))).padStart(2, '0');
            const minutes = String(Math.floor((diffInMs % (1000 * 60 * 60)) / (1000 * 60))).padStart(2, '0');
            const seconds = String(Math.floor((diffInMs % (1000 * 60)) / 1000)).padStart(2, '0');
            return { text: `${hours}:${minutes}:${seconds}`, isUpcoming: true };
        } else {
            const overdueMs = Math.abs(diffInMs);
            const hours = String(Math.floor(overdueMs / (1000 * 60 * 60))).padStart(2, '0');
            const minutes = String(Math.floor((overdueMs % (1000 * 60 * 60)) / (1000 * 60))).padStart(2, '0');
            const seconds = String(Math.floor((overdueMs % (1000 * 60)) / 1000)).padStart(2, '0');
            return { text: `-${hours}:${minutes}:${seconds}`, isUpcoming: false };
        }
    };

    const displayedRecords = records.filter((record) =>
        (record.patientName || '').toLowerCase().includes(patientFilter.toLowerCase())
    );

    // Sort appointments in descending order so the latest appointments appear first (same as IP)
    const sortedFilteredRecords = [...displayedRecords].sort((a, b) => {
        if (waitTimeSortOrder) {
            const wtA = parseFloat(a.waitTime) || 0;
            const wtB = parseFloat(b.waitTime) || 0;
            return waitTimeSortOrder === 'asc' ? wtA - wtB : wtB - wtA;
        }
        const dateA = a.appointmentDate ? new Date(`${a.appointmentDate}T${a.timeSlot || '00:00:00'}`).getTime() : 0;
        const dateB = b.appointmentDate ? new Date(`${b.appointmentDate}T${b.timeSlot || '00:00:00'}`).getTime() : 0;
        if (dateA !== dateB && !isNaN(dateA) && !isNaN(dateB)) {
            return dateB - dateA;
        }
        return (Number(b.appointmentId) || 0) - (Number(a.appointmentId) || 0);
    });

    const getStatusStyle = (record) => {
        const status = (record.appointmentStatus || record.status || 'SCHEDULED').toUpperCase();
        switch (status) {
            case 'COMPLETED': return { color: '#15803d', background: '#dcfce7', border: '1px solid #bbf7d0' };
            case 'CANCELLED': return { color: '#b91c1c', background: '#fee2e2', border: '1px solid #fca5a5' };
            case 'SCHEDULED': return { color: '#0369a1', background: '#e0f2fe', border: '1px solid #bae6fd' };
            default:          return { color: '#0369a1', background: '#e0f2fe', border: '1px solid #bae6fd' };
        }
    };

    const getStatusLabel = (record) => {
        return record.appointmentStatus || record.status || 'SCHEDULED';
    };

    return (
        <div className="unified-master-card">
          <div className="master-card-accent-bar" />

          {/* Master Toolbar Header: Search + Date Picker + CTA */}
          <div className="master-toolbar-header">
            <div className="toolbar-left-group">
              <div className="toolbar-search-box">
                <SearchBar onSearch={onSearch} />
              </div>
              <div className="toolbar-date-picker">
                <DateRangePickerComponent startDate={startDate} endDate={endDate} onDateChange={onDateChange} />
              </div>
            </div>

            <button className="master-new-appt-btn" onClick={onNewAppointment}>
              <FaPlus size={13} /> New Appointment
            </button>
          </div>

          {/* Unified Tabs & Status Pills Navigation */}
          <div className="master-tabs-bar">
            <div className="tabs-list">
              <button
                className={`tab-btn ${activeTab === "Pending Appointments" ? "active-tab-btn" : ""}`}
                onClick={() => setActiveTab("Pending Appointments")}
              >
                Pending Appointments ({counts.pending || 0})
              </button>
              <button
                className={`tab-btn ${activeTab === "Completed Appointments" ? "active-tab-btn" : ""}`}
                onClick={() => setActiveTab("Completed Appointments")}
              >
                Completed Appointments ({counts.completed || 0})
              </button>
              <button
                className={`tab-btn ${activeTab === "Future Appointments" ? "active-tab-btn" : ""}`}
                onClick={() => setActiveTab("Future Appointments")}
              >
                Future Appointments ({counts.future || 0})
              </button>
            </div>

            <div className="status-chips-list">
              <span className="status-chip waiting-chip">Waiting: {counts.pending || 0}</span>
              <span className="status-chip completed-chip">Completed: {counts.completed || 0}</span>
              <span className="status-chip cancelled-chip">Cancelled: {counts.cancelled || 0}</span>
            </div>
          </div>

          {/* Table Container */}
          <div className="master-table-container">
            {loading ? (
              <div className="table-loading-state">
                <FaClock size={32} color="#64748b" />
                <p>Loading Outpatient Appointments...</p>
              </div>
            ) : (
              <table className="master-data-table">
                <thead>
                  <tr>
                    <th>Time</th>
                    <th>Type</th>
                    <th style={{ position: 'relative' }}>
                      <div
                        style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', gap: '6px' }}
                        onClick={() => setShowPatientFilter((prev) => !prev)}
                      >
                        Patient <FaFilter size={11} color="#64748b" />
                      </div>
                      {showPatientFilter && (
                        <div className="table-header-popover" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="text"
                            value={patientFilter}
                            onChange={(e) => setPatientFilter(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            placeholder="Filter by patient name..."
                            className="header-filter-input"
                            autoFocus
                          />
                        </div>
                      )}
                    </th>
                    <th>MR Number</th>
                    <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={toggleWaitTimeSort}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        Wait Time
                        {waitTimeSortOrder === 'asc' ? (
                          <ArrowUp size={14} strokeWidth={2.5} />
                        ) : waitTimeSortOrder === 'desc' ? (
                          <ArrowDown size={14} strokeWidth={2.5} />
                        ) : (
                          <ArrowUpDown size={14} strokeWidth={2} color="#94a3b8" />
                        )}
                      </div>
                    </th>
                    <th>Due Amount</th>
                    <th>Status</th>
                  </tr>
                </thead>

                {sortedFilteredRecords.length === 0 ? (
                  <tbody>
                    <tr>
                      <td colSpan="7">
                        <div className="table-empty-state">
                          <FaCalendarTimes size={38} color="#94a3b8" />
                          <p>No appointments found for the selected criteria.</p>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                ) : (
                  <tbody>
                    {sortedFilteredRecords.map((record, index) => (
                      <tr
                        key={index}
                        onClick={() => onSelectPatient(record)}
                        onMouseEnter={(e) => handleRowMouseEnter(e, index)}
                        onMouseLeave={handleRowMouseLeave}
                        className={`table-data-row ${(record.flagged || record.isFlagged) ? 'flagged-row' : ''} ${record.emergency ? 'emergency-row' : ''}`}
                      >
                        <td className="time-cell" style={{ position: 'relative' }}>
                          {dateTimeFormatter(record.appointmentDate, record.timeSlot)}

                          {/* Dynamic Screen-Aware Hover Photo Lightbox */}
                          {hoveredRowIndex === index && !showPatientFilter && (
                            <div
                              className={`patient-hover-popover ${popoverDirMap[index] === 'above' ? 'popover-above' : 'popover-below'}`}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <div className="popover-avatar-wrap">
                                <img
                                  src={record.photo ? `data:image/jpeg;base64,${record.photo}` : "/VThospitalsIcon.png"}
                                  alt={record.patientName}
                                  className="popover-avatar-img"
                                />
                              </div>
                            </div>
                          )}
                        </td>
                        <td><span className="type-badge">{record.bookingType}</span></td>
                        <td className="patient-name-cell">
                          <div className="patient-avatar-circle">
                            {record.photo ? (
                              <img src={`data:image/jpeg;base64,${record.photo}`} alt={record.patientName} className="patient-avatar-img" />
                            ) : (
                              <FaUser size={12} color="#2563eb" />
                            )}
                          </div>
                          <span className="patient-fullname">{record.patientName}</span>
                          {record.emergency && (
                            <span className="emergency-badge-chip">🚨 Emergency</span>
                          )}
                        </td>
                        <td className="mrn-code-cell">{record.mrNumber}</td>
                        <td>
                          {(() => {
                            const { text, isUpcoming } = calculateWaitTime(record.appointmentDate, record.timeSlot);
                            return (
                              <span className={`wait-time-pill ${isUpcoming ? 'upcoming' : 'overdue'}`}>
                                {text}
                              </span>
                            );
                          })()}
                        </td>
                        <td className="due-amount-cell">
                          {record.billClearance?.totalDueAmount != null ? `₹${record.billClearance.totalDueAmount}` : '₹0'}
                        </td>
                        <td>
                          <span style={{
                            ...getStatusStyle(record),
                            fontWeight: '700',
                            fontSize: '11px',
                            padding: '4px 10px',
                            borderRadius: '12px',
                            display: 'inline-block',
                            letterSpacing: '0.3px',
                            textTransform: 'uppercase',
                          }}>
                            {getStatusLabel(record)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                )}
              </table>
            )}
          </div>

          {/* Master Pagination Footer */}
          <div className="master-pagination-footer">
            <span className="pagination-summary-text">
              Showing page <strong>{page + 1}</strong> of <strong>{totalPages || 1}</strong> ({totalElements} appointments)
            </span>
            <Pagination
              count={totalPages}
              page={page + 1}
              onChange={(e, value) => setPage(value - 1)}
              renderItem={(item) => (
                <PaginationItem
                  {...item}
                  components={{
                    previous: () => <span>Previous</span>,
                    next: () => <span>Next</span>,
                  }}
                />
              )}
              color="primary"
              variant="outlined"
              shape="rounded"
            />
          </div>
        </div>
    );
}